import { useEffect, useMemo, useState } from "react";
import { api } from "./api";
import type { MiniApp } from "./types";

type MiniAppValue = string | number | boolean | null;

function initialValues(app: MiniApp): Record<string, MiniAppValue> {
  return Object.fromEntries(
    (app.content.fields ?? []).map((field) => [
      field.id,
      field.value ?? (field.type === "checkbox" ? false : ""),
    ]),
  );
}

export function MiniAppCard({
  app,
  onUpdate,
}: {
  app: MiniApp;
  onUpdate: (app: MiniApp) => void;
}) {
  const [values, setValues] = useState<Record<string, MiniAppValue>>(() => initialValues(app));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const active = app.status === "active";
  const report = app.kind === "report";

  const visibleRows = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase();
    const rows = query
      ? (app.content.rows ?? []).filter((row) => Object.values(row).some((value) => String(value ?? "").toLocaleLowerCase().includes(query)))
      : [...(app.content.rows ?? [])];
    if (!sort) return rows;
    return rows.sort((left, right) => {
      const comparison = String(left[sort.key] ?? "").localeCompare(
        String(right[sort.key] ?? ""),
        undefined,
        { numeric: true },
      );
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [app.content.rows, filter, sort]);

  useEffect(() => {
    setValues(initialValues(app));
    setFilter("");
    setSort(null);
  }, [app]);

  const interact = async (input: Parameters<typeof api.interactWithMiniApp>[1]) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.interactWithMiniApp(app.id, input);
      onUpdate(result.miniApp);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The mini app could not be updated.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`mini-app mini-app-${app.kind} status-${app.status}`}>
      <header>
        <div>
          <span>{report ? "PARAPLUS REPORT" : `PARAPLUS APP · ${app.kind.replace("_", " ")}`}</span>
          <h4>{app.title}</h4>
        </div>
        <b>{report ? "REPORT" : app.status}</b>
      </header>
      {app.description && <p className="mini-app-description">{app.description}</p>}
      {app.content.metrics && (
        <div className="mini-app-metrics">
          {app.content.metrics.map((metric) => (
            <div key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
              {metric.detail && <small>{metric.detail}</small>}
            </div>
          ))}
        </div>
      )}
      {app.content.columns && app.content.rows && (
        <div className="mini-app-table-wrap">
          {report && app.content.rows.length > 5 && (
            <div className="mini-app-report-tools">
              <input
                aria-label={`Filter ${app.title}`}
                placeholder="Filter report rows"
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
              />
              <span>{visibleRows.length} of {app.content.rows.length} rows</span>
            </div>
          )}
          <table>
            <thead><tr>{app.content.columns.map((column) => (
              <th key={column.key}>
                {report ? (
                  <button
                    type="button"
                    onClick={() => setSort((current) => current?.key === column.key
                      ? { key: column.key, direction: current.direction === "asc" ? "desc" : "asc" }
                      : { key: column.key, direction: "asc" })}
                  >
                    {column.label}{sort?.key === column.key ? (sort.direction === "asc" ? " ↑" : " ↓") : ""}
                  </button>
                ) : column.label}
              </th>
            ))}</tr></thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={index}>
                  {app.content.columns?.map((column) => <td key={column.key}>{String(row[column.key] ?? "")}</td>)}
                </tr>
              ))}
              {visibleRows.length === 0 && (
                <tr><td className="mini-app-empty" colSpan={app.content.columns.length}>No matching rows.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {app.content.items && (
        <div className="mini-app-items">
          {app.content.items.map((item) => (
            <button
              key={item.id}
              disabled={!active || busy || report}
              onClick={() => void interact({
                expectedVersion: app.concurrencyVersion,
                intent: "toggle_item",
                itemId: item.id,
              })}
            >
              <i className={item.status ?? "pending"}>
                {item.status === "complete" ? "✓" : item.status === "attention" ? "!" : ""}
              </i>
              <span>
                <strong>{item.label}</strong>
                {item.detail && <small>{item.detail}</small>}
              </span>
            </button>
          ))}
        </div>
      )}
      {app.content.links && (
        <div className="mini-app-links">
          {app.content.links.map((link) => (
            <a key={`${link.url}:${link.label}`} href={link.url} target="_blank" rel="noreferrer">
              <strong>{link.label}</strong>
              {link.detail && <small>{link.detail}</small>}
            </a>
          ))}
        </div>
      )}
      {app.content.fields && (
        <div className="mini-app-fields">
          {app.content.fields.map((field) => (
            <label key={field.id}>
              <span>{field.label}{field.required ? " *" : ""}</span>
              {field.type === "textarea" ? (
                <textarea
                  disabled={!active}
                  value={String(values[field.id] ?? "")}
                  onChange={(event) => setValues({ ...values, [field.id]: event.target.value })}
                />
              ) : field.type === "select" ? (
                <select
                  disabled={!active}
                  value={String(values[field.id] ?? "")}
                  onChange={(event) => setValues({ ...values, [field.id]: event.target.value })}
                >
                  <option value="">Select…</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              ) : field.type === "checkbox" ? (
                <input
                  type="checkbox"
                  disabled={!active}
                  checked={Boolean(values[field.id])}
                  onChange={(event) => setValues({ ...values, [field.id]: event.target.checked })}
                />
              ) : (
                <input
                  type={field.type}
                  disabled={!active}
                  value={String(values[field.id] ?? "")}
                  onChange={(event) => setValues({
                    ...values,
                    [field.id]: field.type === "number" && event.target.value !== ""
                      ? Number(event.target.value)
                      : event.target.value,
                  })}
                />
              )}
            </label>
          ))}
        </div>
      )}
      {error && <p className="mini-app-error">{error}</p>}
      {active && app.actions.length > 0 && (
        <footer>
          {app.actions.map((action) => (
            <button
              className={action.style}
              key={action.id}
              disabled={busy}
              onClick={() => void interact({
                expectedVersion: app.concurrencyVersion,
                intent: action.behavior,
                actionId: action.id,
                values: action.behavior === "submit" ? values : undefined,
              })}
            >
              {busy ? "Working…" : action.label}
            </button>
          ))}
        </footer>
      )}
    </section>
  );
}
